import { Injectable } from '@angular/core';
import { ITableHeader } from '../../../../../constants.model';
import { HelperService } from '../../helper.service';
import moment from 'moment';
import { IFieldMetaDropdown } from '../../../../view/settings/form/form.model';
import { TranslateService } from '@ngx-translate/core';
import { IFieldVersion } from '../../../../view/reports/form-templates/form-templates.model';
import _ from 'lodash';
import { IFieldMetaField, ITemplateData } from './templates-helper.model';
import { IConfigurableFormFieldMeta } from '../../form-fields/form-fields-helper.model';
import { EFieldsTypes } from '../../../../view/settings/master-data/form-master-data/form-master-data.component.model';

@Injectable({ providedIn: 'root' })
export class TemplatesHelperService {
  constructor(private readonly translate: TranslateService, private readonly helperService: HelperService) {}

  private readonly masterDataItemPropertyPrefix: 'master_data_item_' = 'master_data_item_';
  private readonly prefilledFieldItemPropertyPrefix: 'prefilled_field_item_' = 'prefilled_field_item_';

  public formatTemplateMasterDataValues(
    templateData: (unknown & ITemplateData)[],
    masterDataColumns: ITableHeader[],
  ): any[] {
    return templateData.map((data: unknown & ITemplateData): unknown & ITemplateData => {
      let fieldMetaValues: { [key: string]: string } = {};

      data.fieldMeta.forEach((field: IFieldMetaField): void => {
        const fieldKey: string = field.fieldId.split('_').splice(2, field.fieldId.split('_').length).join('_');

        for (const item of masterDataColumns) {
          if (fieldKey === item.originalValue) {
            const formattedFieldMetaValue: string = this.formatColumnInputModel(field, item.fieldType);
            const fieldMetaItemKey: string = item.value;
            const fieldMetaIteValue: string = !fieldMetaValues[fieldMetaItemKey]
              ? formattedFieldMetaValue
              : `${fieldMetaValues[fieldMetaItemKey]}${!formattedFieldMetaValue ? '' : `, ${formattedFieldMetaValue}`}`;

            fieldMetaValues = {
              ...fieldMetaValues,
              [fieldMetaItemKey]: fieldMetaIteValue,
            };

            break;
          }
        }
      });

      return {
        ...data,
        ...fieldMetaValues,
      };
    });
  }

  public formatTemplatePrefilledFieldValues(
    templateData: (unknown & ITemplateData)[],
    prefilledFieldColumns: ITableHeader[],
  ): any[] {
    return templateData.map((data: unknown & ITemplateData): unknown & ITemplateData => {
      let prefilledFieldValues: { [key: string]: string } = {};

      (data.selectableFieldsMeta ?? []).forEach((fieldMeta: IConfigurableFormFieldMeta): void => {
        if (!fieldMeta.prefilled) {
          return;
        }

        for (const item of prefilledFieldColumns) {
          if (fieldMeta.fieldId === item.formattedValue) {
            prefilledFieldValues = {
              ...prefilledFieldValues,
              [item.value]: this.formatColumnInputModel(
                { fieldId: fieldMeta.fieldId, inputModel: fieldMeta.prefilledValue },
                item.fieldType,
              ),
            };

            break;
          }
        }
      });

      return {
        ...data,
        ...prefilledFieldValues,
      };
    });
  }

  public formatTemplateColumns(masterDataItems: IFieldVersion[]): [ITableHeader[], ITableHeader[]] {
    return this.buildTemplateColumns(masterDataItems, false);
  }

  public formatTemplatePrefilledFieldColumns(masterDataItems: IFieldVersion[]): [ITableHeader[], ITableHeader[]] {
    return this.buildTemplateColumns(masterDataItems, true);
  }

  private buildTemplateColumns(
    masterDataItems: IFieldVersion[],
    isSelectable: boolean,
  ): [ITableHeader[], ITableHeader[]] {
    const tableHeaders: ITableHeader[] = [];
    const propertyPrefix: string = isSelectable
      ? this.prefilledFieldItemPropertyPrefix
      : this.masterDataItemPropertyPrefix;
    const nameSuffix: string = isSelectable
      ? ` (${this.translate.instant('general.subtitles.selectableFormEntryFields')})`
      : '';

    masterDataItems.forEach((item: IFieldVersion) => {
      if (!item.meta || Boolean(item.isSelectable) !== isSelectable) {
        return;
      }

      const fieldIdSplit: string[] = item.meta.fieldId.split('_');
      const fieldIdKey: string = `${propertyPrefix}${_.snakeCase(item.name.toLowerCase())}`;

      tableHeaders.push({
        originalValue: fieldIdSplit.splice(2, fieldIdSplit.length).join('_'),
        value: fieldIdKey,
        name: `${item.name}${nameSuffix}`,
        sortable: false,
        formattedValue: item.meta.fieldId,
        fieldType: item.meta.type,
      });
    });

    const uniqueTableHeaders: ITableHeader[] = _.uniqBy(tableHeaders, 'value');

    return [uniqueTableHeaders, tableHeaders];
  }

  private formatColumnInputModel(data: IFieldMetaField, fieldType?: string): string {
    const inputModel = data.inputModel;

    switch (typeof inputModel) {
      case 'boolean':
        return inputModel ? this.translate.instant('general.checked') : this.translate.instant('general.unChecked');
      case 'string':
        return fieldType === EFieldsTypes.DECIMAL
          ? this.helperService.toUserDecimalSeparator(inputModel) ?? ''
          : inputModel;
      case 'number':
        return String(inputModel);
      case 'object':
        if (inputModel && !Array.isArray(inputModel)) {
          const timezone: string = HelperService.userTimeZone ?? 'utc';

          return moment(inputModel.startDate).tz(timezone).format(HelperService.userDateTimeFormat);
        }

        if (fieldType !== 'CheckboxMulti') {
          return inputModel?.map((key: IFieldMetaDropdown) => key.label).join(', ');
        }

        return inputModel
          ?.filter((key: IFieldMetaDropdown) => key.value)
          .map((key: IFieldMetaDropdown) => key.label)
          .join(', ');
      default:
        return inputModel;
    }
  }
}
